import React, { useState, useEffect, useMemo, createContext, useContext, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { LanguageProvider, useLanguage } from './i18n/LanguageContext';
import { getIdFromPath, getUrlForId } from './i18n/routes';
import PageScrollHandler from './components/ScrollToTop';
import { isWhitelistedSlug } from './data/whitelistedSlugs';
const GonePage = React.lazy(() => import('./components/GonePage'));

// Sadece kullanılan ikonları import et (tree shaking için)
import { Calculator as LucideCalculator, Sun, Moon } from 'lucide-react';

import { urlMappings } from './data/urlMappings';

const Sidebar = React.lazy(() => import('./components/Sidebar'));
const HomePage = React.lazy(() => import('./components/HomePage'));

// Lazy-loaded calculator components for code splitting
const AreaCalculator = React.lazy(() => import('./components/calculators/AreaCalculator'));
const GoldenRatioCalculator = React.lazy(() => import('./components/calculators/GoldenRatioCalculator'));
const PrimeFactorCalculator = React.lazy(() => import('./components/calculators/PrimeFactorCalculator'));
const PerimeterCalculator = React.lazy(() => import('./components/calculators/PerimeterCalculator'));
const GcdLcmCalculator = React.lazy(() => import('./components/calculators/GcdLcmCalculator'));
const FactorialCalculator = React.lazy(() => import('./components/calculators/FactorialCalculator'));
const VolumeCalculator = React.lazy(() => import('./components/calculators/VolumeCalculator'));
const BaseConversionCalculator = React.lazy(() => import('./components/calculators/BaseConversionCalculator'));
const PowerCalculator = React.lazy(() => import('./components/calculators/PowerCalculator'));
const RandomNumberGenerator = React.lazy(() => import('./components/calculators/RandomNumberGenerator'));
// Percentage components moved to subdomain
// Pronunciation components moved to subdomain
const WeightConverterPage = React.lazy(() => import('./components/calculators/WeightConverterPage'));
const LengthConverterPage = React.lazy(() => import('./components/calculators/LengthConverterPage'));
const StandardDeviationCalculator = React.lazy(() => import('./components/calculators/StandardDeviationCalculator'));
const CombinationCalculator = React.lazy(() => import('./components/calculators/CombinationCalculator'));
const PermutationCalculator = React.lazy(() => import('./components/calculators/PermutationCalculator'));
const RatioCalculator = React.lazy(() => import('./components/calculators/RatioCalculator'));
const SquareMeterCalculator = React.lazy(() => import('./components/calculators/SquareMeterCalculator'));
const InchCalculator = React.lazy(() => import('./components/calculators/InchCalculator'));
const MileCalculator = React.lazy(() => import('./components/calculators/MileCalculator'));
const ModularCalculator = React.lazy(() => import('./components/calculators/ModularCalculator'));
const WaterNeedCalculator = React.lazy(() => import('./components/calculators/WaterNeedCalculator'));
const IdealWeightCalculator = React.lazy(() => import('./components/calculators/IdealWeightCalculator'));
const CalorieNeedCalculator = React.lazy(() => import('./components/calculators/CalorieNeedCalculator'));
const MacroNutrientCalculator = React.lazy(() => import('./components/calculators/MacroNutrientCalculator'));
const ProteinNeedCalculator = React.lazy(() => import('./components/calculators/ProteinNeedCalculator'));
const CarbNeedCalculator = React.lazy(() => import('./components/calculators/CarbNeedCalculator'));
const FatNeedCalculator = React.lazy(() => import('./components/calculators/FatNeedCalculator'));
const BabyHeightCalculator = React.lazy(() => import('./components/calculators/BabyHeightCalculator'));
const BmrCalculator = React.lazy(() => import('./components/calculators/BmrCalculator'));
const BmiCalculator = React.lazy(() => import('./components/calculators/BmiCalculator'));
const BodyFatCalculator = React.lazy(() => import('./components/calculators/BodyFatCalculator'));
const WaistHipRatioCalculator = React.lazy(() => import('./components/calculators/WaistHipRatioCalculator'));
const BabyWeightCalculator = React.lazy(() => import('./components/calculators/BabyWeightCalculator'));
const BirthDateCalculator = React.lazy(() => import('./components/calculators/BirthDateCalculator'));
const SmokingCostCalculator = React.lazy(() => import('./components/calculators/SmokingCostCalculator'));
const ZekatCalculator = React.lazy(() => import('./components/calculators/ZekatCalculator'));
const WordCountCalculator = React.lazy(() => import('./components/calculators/WordCountCalculator'));
const DaysCalculator = React.lazy(() => import('./components/calculators/DaysCalculator'));
const GradeCalculator = React.lazy(() => import('./components/calculators/GradeCalculator'));
const GpaCalculator = React.lazy(() => import('./components/calculators/GpaCalculator'));
const Calculator = React.lazy(() => import('./components/calculators/Calculator'));
const PriceIncreaseCalculator = React.lazy(() => import('./components/calculators/PriceIncreaseCalculator'));
const KlimaBtuCalculator = React.lazy(() => import('./components/calculators/KlimaBtuCalculator'));
const YasHesaplamaCalculator = React.lazy(() => import('./components/calculators/YasHesaplamaCalculator'));
const AgeCalculationPage = React.lazy(() => import('./components/calculators/AgeCalculationPage'));
const LiseMezuniyetPuaniCalculator = React.lazy(() => import('./components/calculators/LiseMezuniyetPuaniCalculator'));
const MetabolizmaYasiCalculator = React.lazy(() => import('./components/calculators/MetabolizmaYasiCalculator'));
const YumurtlamaDonemiCalculator = React.lazy(() => import('./components/calculators/YumurtlamaDonemiCalculator'));
const ObpCalculator = React.lazy(() => import('./components/calculators/ObpCalculator'));
const TusPuanCalculator = React.lazy(() => import('./components/calculators/TusPuanCalculator'));
const TytPuanCalculator = React.lazy(() => import('./components/calculators/TytPuanCalculator'));
const BraSizeCalculator = React.lazy(() => import('./components/calculators/BraSizeCalculator'));
const DesiCalculator = React.lazy(() => import('./components/calculators/DesiCalculator'));
const DolarTlCalculator = React.lazy(() => import('./components/calculators/DolarTlCalculator'));
const DollarEuroCalculator = React.lazy(() => import('./components/calculators/DollarEuroCalculator'));
const TlDolarCalculator = React.lazy(() => import('./components/calculators/TlDolarCalculator'));
const EuroDolarCalculator = React.lazy(() => import('./components/calculators/EuroDolarCalculator'));
const TlEuroCalculator = React.lazy(() => import('./components/calculators/TlEuroCalculator'));
const EuroTlCalculator = React.lazy(() => import('./components/calculators/EuroTlCalculator'));
const LoremIpsumGenerator = React.lazy(() => import('./components/calculators/LoremIpsumGenerator'));
const CryptoCalculator = React.lazy(() => import('./components/calculators/CryptoCalculator'));
const CryptoCurrencyConverter = React.lazy(() => import('./components/calculators/CryptoCurrencyConverter'));
const YoldaGecenZamanCalculator = React.lazy(() => import('./components/calculators/YoldaGecenZamanCalculator'));
const DataUnitConverter = React.lazy(() => import('./components/calculators/DataUnitConverter'));
const WeightUnitConverter = React.lazy(() => import('./components/calculators/WeightUnitConverter'));
const EnergyUnitConverter = React.lazy(() => import('./components/calculators/EnergyUnitConverter'));
const LengthUnitConverter = React.lazy(() => import('./components/calculators/LengthUnitConverter'));
const DailyRentalIncomeCalculator = React.lazy(() => import('./components/calculators/DailyRentalIncomeCalculator'));
const ElektrikFaturasiCalculator = React.lazy(() => import('./components/calculators/ElektrikFaturasiCalculator'));
const DoğalgazFaturasiCalculator = React.lazy(() => import('./components/calculators/DoğalgazFaturasiCalculator'));
const JsonFormatterCalculator = React.lazy(() => import('./components/calculators/JsonFormatterCalculator'));
const TextCaseConverter = React.lazy(() => import('./components/calculators/TextCaseConverter'));
const UrlEncodeDecodeCalculator = React.lazy(() => import('./components/calculators/UrlEncodeDecodeCalculator'));
const Base64EncodeDecodeCalculator = React.lazy(() => import('./components/calculators/Base64EncodeDecodeCalculator'));
const UuidGeneratorCalculator = React.lazy(() => import('./components/calculators/UuidGeneratorCalculator'));
const HashCalculatorCalculator = React.lazy(() => import('./components/calculators/HashCalculatorCalculator'));
const QrCodeGeneratorCalculator = React.lazy(() => import('./components/calculators/QrCodeGeneratorCalculator'));
const ImageResizerCalculator = React.lazy(() => import('./components/calculators/ImageResizerCalculator'));
const TextFormatterCalculator = React.lazy(() => import('./components/calculators/TextFormatterCalculator'));
const PasswordGeneratorCalculator = React.lazy(() => import('./components/calculators/PasswordGeneratorCalculator'));
const ColorPickerCalculator = React.lazy(() => import('./components/calculators/ColorPickerCalculator'));
const TimeZoneConverterCalculator = React.lazy(() => import('./components/calculators/TimeZoneConverterCalculator'));
const CSSMinifierCalculator = React.lazy(() => import('./components/calculators/CSSMinifierCalculator'));
const JSBeautifierCalculator = React.lazy(() => import('./components/calculators/JSBeautifierCalculator'));
const TextToASCIICalculator = React.lazy(() => import('./components/calculators/TextToASCIICalculator'));
const ImageCompressorCalculator = React.lazy(() => import('./components/calculators/ImageCompressorCalculator'));
const ImageFormatConverterCalculator = React.lazy(() => import('./components/calculators/ImageFormatConverterCalculator'));
const FuelConsumptionCalculator = React.lazy(() => import('./components/calculators/FuelConsumptionCalculator'));
const AnimalSoundsPage = React.lazy(() => import('./components/tools/AnimalSoundsPage'));
const FaviconGeneratorCalculator = React.lazy(() => import('./components/calculators/FaviconGeneratorCalculator'));
const TurkishCharacterCleanerCalculator = React.lazy(() => import('./components/calculators/TurkishCharacterCleanerCalculator'));
const AboutPage = React.lazy(() => import('./pages/AboutPage'));
const ContactPage = React.lazy(() => import('./pages/ContactPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/PrivacyPolicyPage'));

// Loading fallback component - LCP optimizasyonu için minimal skeleton
const LoadingFallback = () => (
  <div className="p-8 flex flex-col items-center justify-center min-h-[400px]">
    <div className="w-full max-w-md space-y-4">
      <div className="h-8 bg-cyan-900/30 rounded animate-pulse"></div>
      <div className="h-4 bg-cyan-900/20 rounded w-3/4 animate-pulse"></div>
      <div className="h-4 bg-cyan-900/20 rounded w-1/2 animate-pulse"></div>
    </div>
    <div className="mt-4 text-cyan-400 text-sm">Yükleniyor...</div>
  </div>
);


// ID'den URL'ye çevirme fonksiyonu
const getUrlFromId = (id: string, lang: 'tr' | 'nl' = 'tr'): string => {
  return getUrlForId(id, lang) || urlMappings[id as keyof typeof urlMappings] || `/${id}`;
};

// URL'den ID'ye çevirme fonksiyonu (Legacy support + new i18n support)
const getIdFromUrl = (url: string): string => {
  // First try the new i18n route system
  const i18nId = getIdFromPath(url);
  if (i18nId) return i18nId;

  // Fallback to legacy mapping logic for TR routes not yet in i18n config
  const entries = Object.entries(urlMappings);
  for (const [id, mappedUrl] of entries) {
    if (mappedUrl === url) return id;
  }

  // Alt sayfalar için özel kontrol — sadece whitelist'teki slug'lar
  if (url.match(/^\/sayi-okunusu\/.+$/)) {
    const slug = url.replace('/sayi-okunusu/', '');
    if (isWhitelistedSlug('pronunciation', slug)) return 'pronunciation';
    return 'gone410';
  }

  if (url.match(/^\/koklu-sayi\/.+$/)) {
    return 'sqrt';
  }
  if (url.match(/^\/yuzde-hesaplama\/.+$/)) {
    const slug = url.replace('/yuzde-hesaplama/', '');
    if (isWhitelistedSlug('percentage', slug)) return 'percentage';
    return 'gone410';
  }

  if (url.match(/^\/agirlik-cevirici\/.+$/)) {
    return 'weightUnitConverter';
  }

  if (url.match(/^\/uzunluk-birimi-donusturucu\/.+$/)) {
    return 'lengthUnitConverter';
  }

  // Eğer tam URL match etmiyorsa, ID tabanlı URL'leri de kontrol et
  // Örnek: /lorem-ipsum → loremIpsum ID'sini döndür
  const urlWithoutSlash = url.replace(/^\//, ''); // Başındaki /'ı kaldır

  // ID'lerle doğrudan eşleşme kontrolü
  const idMappings: { [key: string]: string } = {
    'lorem-ipsum': 'loremIpsum',
    'dataUnitConverter': 'dataUnitConverter',
    'weightUnitConverter': 'weightUnitConverter',
    'energyUnitConverter': 'energyUnitConverter',
    'lengthUnitConverter': 'lengthUnitConverter',
    'metabolizmaYasi': 'metabolizmaYasi',
    'kelime-sayisi': 'wordCount',
    'sayi-okunusu': 'pronunciation',
    'veri-birimi-cevirici': 'dataUnitConverter',
    'agirlik-birimi-donusturucu': 'weightUnitConverter',
    'enerji-birimi-donusturucu': 'energyUnitConverter',
    'uzunluk-birimi-donusturucu': 'lengthUnitConverter',
    'lorem-ipsum-uretici': 'loremIpsum',
    'base64-encode-decode': 'base64EncodeDecode',
    'turkce-karakter-silme-temizleme-duzeltme': 'turkishCharCleaner',
    'hakkinda': 'about',
    'iletisim': 'contact',
    'gizlilik-politikasi': 'privacyPolicy',
    'kelime-sayisi-hesaplama': 'wordCount',
    'json-bicimlendirici': 'jsonFormatter',
    'metin-harf-donusturucu': 'textCaseConverter',
    'url-encode-decode': 'urlEncodeDecoder',
    'uuid-generator': 'uuidGenerator',
    'hash-hesaplayici': 'hashCalculator',
    'qr-kod-olusturucu': 'qrCodeGenerator',
    'gorsel-boyutlandirma': 'imageResizer',
    'metin-bicimlendirici': 'textFormatter',
    'sifre-uretici': 'passwordGenerator',
    'renk-secici': 'colorPicker',
    'saat-dilimi-donusturucu': 'timeZoneConverter',
    'css-minifier': 'cssMinifier',
    'js-beautifier': 'jsBeautifier',
    'text-to-ascii': 'textToASCII',
    'image-compressor': 'imageCompressor',
    'image-format-converter': 'imageFormatConverter',
    'favicon-generator': 'faviconGenerator',
    'yakit-tuketimi-hesaplama': 'fuelConsumption',
    'hayvan-sesleri': 'animalSounds',
    'gunluk-ev-kiralama-kazanc-hesaplama': 'dailyRentalIncome',
    'elektrik-faturasi-hesaplama': 'elektrikFaturasi',
    'dogalgaz-faturasi-hesaplama': 'dogalgazFaturasi',
    'ideal-kilo': 'idealWeight',
    'vucut-kitle-indeksi': 'bmi',
    'vucut-yag-orani': 'bodyFat',
    'bel-kalca-orani': 'waistHipRatio',
    'bazal-metabolizma-hizi': 'bmr',
    'gunluk-kalori-ihtiyaci': 'calorieNeed',
    'makro-besin': 'macroNutrient',
    'protein-ihtiyaci': 'proteinNeed',
    'karbonhidrat-ihtiyaci': 'carbNeed',
    'yag-ihtiyaci': 'fatNeed',
    'bebek-boyu': 'babyHeight',
    'bebek-kilosu': 'babyWeight',
    'yumurtlama-donemi-hesaplama': 'yumurtlamaDonemi',
    'sutyen-olcusu-hesaplama': 'braSize',
    'ebob-ekok': 'gcdlcm',
    'uslu-sayi': 'power',
    'moduler': 'modular',
    'asal-carpan': 'primeFactor',
    'standart-sapma': 'stdDev',
    'faktoriyel': 'factorial',
    'kombinasyon': 'combination',
    'permutasyon': 'permutation',
    'oran': 'ratio',
    'rastgele-sayi': 'random',
    'kripto-kar-zarar-hesaplama': 'cryptoCalculator',
    'kripto-para-donusturucu': 'cryptoCurrencyConverter',
    'dolar-tl-donusturucu': 'dollarTlCalculator',
    'tl-dolar-cevirici': 'tlDolarCalculator',
    'dolar-euro-cevirici': 'dollarEuro',
    'euro-dolar-donusturucu': 'euroDolarCalculator',
    'tl-euro-cevirici': 'tlEuroCalculator',
    'euro-tl-donusturucu': 'euroTlCalculator',
    'zam-hesaplama': 'priceIncrease',
    'ders-notu-hesaplama': 'gradeCalculator',
    'vize-final-ortalama-hesaplama': 'gpaCalculator',
    'lise-mezuniyet-puani': 'liseMezuniyet',
    'obp-hesaplama': 'obpCalculator',
    'tus-puan-hesaplama': 'tusPuan',
    'tyt-puan-hesaplama': 'tytPuan',
    'metrekare': 'squareMeter',
    'inc': 'inch',
    'mil': 'mile',
    'taban-donusumu': 'baseConversion',
    'desi-hesaplama': 'desiCalculator',
    'alan-hesaplama': 'area',
    'hacim-hesaplama': 'volume',
    'cevre-hesaplama': 'perimeter',
    'altin-oran': 'goldenRatio',
    'dogum-tarihi': 'birthDate',
    'yolda-gecen-zaman-hesaplama': 'yoldaGecenZaman',
    'klima-btu-hesaplama': 'klimaBtu',
    'zekat-hesaplama': 'zekat'
  };

  if (idMappings[urlWithoutSlash]) {
    return idMappings[urlWithoutSlash];
  }

  return 'home'; // Varsayılan olarak ana sayfa
};

// Theme context
type ThemeContextType = {
  theme: 'light' | 'dark';
  toggleTheme: () => void;
};

export const ThemeContext = createContext<ThemeContextType>({
  theme: 'dark',
  toggleTheme: () => { },
});

export const useTheme = () => useContext(ThemeContext);

// Özel hesaplayıcı logosu bileşeni
const CalculatorLogo: React.FC<{ size?: 'xs' | 'sm' | 'md' }> = ({ size = 'sm' }) => {
  const dimensions = {
    xs: {
      container: 'h-6 w-6 rounded-md',
      screen: 'h-1',
      screenTop: 'top-0.5 left-0.5 right-0.5',
      buttonsContainer: 'space-x-0.5',
      button: 'h-0.5 w-0.5',
      iconSize: 10,
    },
    sm: {
      container: 'h-8 w-8 rounded-md',
      screen: 'h-1.5',
      screenTop: 'top-0.5 left-0.5 right-0.5',
      buttonsContainer: 'space-x-0.5',
      button: 'h-1 w-1',
      iconSize: 12,
    },
    md: {
      container: 'h-12 w-12 rounded-lg',
      screen: 'h-2',
      screenTop: 'top-1 left-1 right-1',
      buttonsContainer: 'space-x-1',
      button: 'h-1.5 w-1.5',
      iconSize: 16,
    }
  };

  const d = dimensions[size];

  return (
    <div className={`relative ${d.container} bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shadow-md`}>
      <div className={`absolute ${d.screenTop} ${d.screen} bg-cyan-300/30 rounded-t-sm`}></div>
      <div className="flex flex-col items-center">
        <div className={`flex ${d.buttonsContainer} mb-0.5`}>
          <div className={`${d.button} bg-white rounded-full`}></div>
          <div className={`${d.button} bg-white rounded-full`}></div>
          <div className={`${d.button} bg-white rounded-full`}></div>
        </div>
        <LucideCalculator size={d.iconSize} className="text-white" />
      </div>
    </div>
  );
};

// ScrollToTop bileşeni - sayfa değiştiğinde scroll'u en üste taşır
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// SEO Meta Etiketleri Bileşeni
function SEOMetaTags({ id, title, description, canonicalUrl }: { id: string, title: string, description: string, canonicalUrl: string }) {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} data-react-helmet="true" />
      <link rel="canonical" href={canonicalUrl} data-react-helmet="true" />

      {/* Open Graph meta etiketleri */}
      <meta property="og:title" content={title} data-react-helmet="true" />
      <meta property="og:description" content={description} data-react-helmet="true" />
      <meta property="og:url" content={canonicalUrl} data-react-helmet="true" />

      {/* Twitter meta etiketleri */}
      <meta name="twitter:title" content={title} data-react-helmet="true" />
      <meta name="twitter:description" content={description} data-react-helmet="true" />

      {/* Schema.org yapısal veri */}
      <script type="application/ld+json">
        {JSON.stringify(
          id === 'home'
            ? {
              "@context": "https://schema.org",
              "@type": "WebSite",
              "url": "https://hesaplama.vip/",
              "name": "Hesaplama.Vip",
              "description": "Modern hesaplama araçları koleksiyonu",
            }
            : {
              "@context": "https://schema.org",
              "@type": "WebApplication",
              "name": title,
              "applicationCategory": "UtilityApplication",
              "url": canonicalUrl,
              "operatingSystem": "Any",
              "offers": {
                "@type": "Offer",
                "price": "0",
                "priceCurrency": "TRY"
              },
              "featureList": "Hesaplama, Dönüştürme, Analiz",
              "browserRequirements": "Requires JavaScript. Requires HTML5."
            }
        )}
      </script>
    </Helmet>
  );
}

// SidebarCalculator tipi (eğer ayrı bir dosyada değilse veya import edilmemişse)
interface SidebarCalculator {
  name: string;
  path: string;
  icon: string; // İkon isimleri string olarak tutuluyor varsayımı
  category: string;
}

export const sidebarCalculators: SidebarCalculator[] = [
  { name: 'Kalori İhtiyacı Hesaplama', path: '/kalori-ihtiyaci', icon: 'flame', category: 'Sağlık' },
  { name: 'Vücut Kitle Endeksi', path: '/vki', icon: 'dumbbell', category: 'Sağlık' },
  { name: 'Metabolizma Yaşı Hesaplama', path: '/metabolizma-yasi', icon: 'activity', category: 'Sağlık' },
  { name: 'Yumurtlama Dönemi Hesaplama', path: '/yumurtlama-donemi-hesaplama', icon: 'calendar-heart', category: 'Sağlık' },
  { name: 'Klima BTU Hesaplama', path: '/klima-btu-hesaplama', icon: 'thermometer-sun', category: 'Ev Araçları' },
  { name: 'Alan Hesaplama (m²)', path: '/alan-hesaplama', icon: 'square', category: 'Ev Araçları' },
  { name: 'Yaş Hesaplama', path: '/yas-hesaplama', icon: 'calendar-days', category: 'Zaman' },
  { name: 'Lise Mezuniyet Puanı Hesaplama', path: '/lise-mezuniyet-puani', icon: 'graduation-cap', category: 'Eğitim' },
  { name: 'TUS Puan Hesaplama', path: '/tus-puan-hesaplama', icon: 'clipboard-edit', category: 'Eğitim' },
  { name: 'TYT Puan Hesaplama', path: '/tyt-puan-hesaplama', icon: 'book-check', category: 'Eğitim' },
  { name: 'Günlük Ev-Bungalov Kiralama Kazanç Hesaplama', path: '/gunluk-ev-kiralama-kazanc-hesaplama', icon: 'home', category: 'Kazanç Tahminleri' },
  { name: 'Elektrik Faturası Hesaplama', path: '/elektrik-faturasi-hesaplama', icon: 'zap', category: 'Ev ve Yaşam' },
  { name: 'Doğalgaz Faturası Hesaplama', path: '/dogalgaz-faturasi-hesaplama', icon: 'flame', category: 'Ev ve Yaşam' },
  { name: 'JSON Biçimlendirici ve Doğrulayıcı', path: '/json-bicimlendirici', icon: 'file-text', category: 'Yardımcı Araçlar' },
  { name: 'Metin Harf Dönüştürücü', path: '/metin-harf-donusturucu', icon: 'type', category: 'Yardımcı Araçlar' },
  { name: 'URL Encode Decode Çevirici', path: '/url-encode-decode', icon: 'link', category: 'Yardımcı Araçlar' },
  { name: 'Base64 Encode Decode Çevirici', path: '/base64-encode-decode', icon: 'file-text', category: 'Yardımcı Araçlar' },
  { name: 'UUID Generator', path: '/uuid-generator', icon: 'hash', category: 'Yardımcı Araçlar' },
  { name: 'Hash Hesaplayıcı - MD5 SHA256 Hash Üretici', path: '/hash-hesaplayici', icon: 'shield', category: 'Yardımcı Araçlar' },
  { name: 'QR Kod Oluşturucu - Online QR Code Generator', path: '/qr-kod-olusturucu', icon: 'qr-code', category: 'Yardımcı Araçlar' },
  { name: 'Görsel Boyutlandırma - Online Image Resizer', path: '/gorsel-boyutlandirma', icon: 'image', category: 'Yardımcı Araçlar' },
  { name: 'Text Formatter - Metin Biçimlendirici ve Düzenleyici', path: '/metin-bicimlendirici', icon: 'file-text', category: 'Yardımcı Araçlar' },
  { name: 'Password Generator - Güçlü Şifre Üreticisi', path: '/sifre-uretici', icon: 'key', category: 'Yardımcı Araçlar' },
  { name: 'Color Picker - Renk Seçici ve Kod Üretici', path: '/renk-secici', icon: 'palette', category: 'Yardımcı Araçlar' },
  { name: 'Time Zone Converter - Saat Dilimi Dönüştürücü', path: '/saat-dilimi-donusturucu', icon: 'clock', category: 'Yardımcı Araçlar' },
  { name: 'CSS Minifier - CSS Sıkıştırıcı ve Optimize Edici', path: '/css-minifier', icon: 'minimize-2', category: 'Yardımcı Araçlar' },
  { name: 'JavaScript Beautifier - JS Kod Düzenleyici ve Formatter', path: '/js-beautifier', icon: 'sparkles', category: 'Yardımcı Araçlar' },
  { name: 'Text to ASCII Art - Metin Sanatı Dönüştürücü', path: '/text-to-ascii', icon: 'type', category: 'Yardımcı Araçlar' },
  { name: 'Image Compressor - Görsel Sıkıştırıcı ve Optimize Edici', path: '/image-compressor', icon: 'image', category: 'Yardımcı Araçlar' },
  { name: 'Görsel Format Dönüştürücü - Resim Formatı Değiştirici', path: '/image-format-converter', icon: 'refresh-cw', category: 'Yardımcı Araçlar' },
  { name: 'Favicon Oluşturucu - Website Simgesi Üretici', path: '/favicon-generator', icon: 'star', category: 'Yardımcı Araçlar' },
  { name: 'Yakıt Tüketimi Hesaplama - Araç Yakıt Masrafı Hesaplayıcı', path: '/yakit-tuketimi-hesaplama', icon: 'fuel', category: 'Otomotiv Hesaplamaları' }
];

function AppContent() {
  const [activeCalculator, setActiveCalculator] = useState<string>('home');
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();


  // Başlangıçta URL'den aktif hesaplayıcıyı belirle
  useEffect(() => {
    // URL'yi kontrol et ve aktif hesaplayıcıyı belirle
    const path = location.pathname;

    // Check for i18n routes first
    const i18nId = getIdFromPath(path);
    if (i18nId) {
      setActiveCalculator(i18nId);
      return;
    }

    // Köklü sayı alt sayfaları — subdomain'e yönlendir
    if (path === '/koklu-sayi' || path.startsWith('/koklu-sayi/')) {
      window.location.href = `https://karekok.hesaplama.vip${path}`;
      return;
    }

    // Dutch sqrt pages — subdomain'e yönlendir
    if (path === '/nl/wortel-berekenen' || path.startsWith('/nl/wortel-berekenen/')) {
      window.location.href = `https://karekok.hesaplama.vip${path}`;
      return;
    }

    // Sayı okunuşu alt sayfaları — whitelist kontrolü
    if (path.match(/^\/sayi-okunusu\/.+$/)) {
      const slug = path.replace('/sayi-okunusu/', '');
      setActiveCalculator(isWhitelistedSlug('pronunciation', slug) ? 'pronunciation' : 'gone410');
      return;
    }

    // Altın hesaplama alt sayfaları — whitelist kontrolü
    if (path.match(/^\/altin-hesaplama\/.+$/)) {
      const slug = path.replace('/altin-hesaplama/', '');
      setActiveCalculator(isWhitelistedSlug('gold', slug) ? 'goldCalculator' : 'gone410');
      return;
    }

    // Yaş hesaplama alt sayfaları için özel kontrol
    if (path.match(/^\/yas-hesaplama\/.+$/)) {
      setActiveCalculator('yasHesaplama');
      return;
    }

    // Kaç gün oldu hesaplama alt sayfaları
    if (path.match(/^\/kac-gun-oldu-hesaplama\/.+$/)) {
      setActiveCalculator('daysCalculator');
      return;
    }

    const id = getIdFromUrl(path) || 'home';
    setActiveCalculator(id);
  }, [location.pathname]);

  // Calculator ID değiştiğinde URL'yi güncelle
  const handleSetActiveCalculator = (id: string) => {
    if (id === 'percentage') {
      window.location.href = 'https://yuzde.hesaplama.vip/';
      return;
    }
    if (id === 'pronunciation') {
      window.location.href = 'https://sayiokunusu.hesaplama.vip/';
      return;
    }
    if (id === 'sqrt') {
      window.location.href = 'https://karekok.hesaplama.vip/';
      return;
    }
    setActiveCalculator(id);
    navigate(getUrlFromId(id, language));
  };

  // Kullanılabilir hesaplayıcıların listesi (Diline göre filtrele)
  const allowedNlIds = ['home', 'percentage', 'pronunciation', 'sqrt', 'dollarEuro'];
  const calculators = [
    { id: 'home', name: t('common.calculator'), icon: 'home' },
    { id: 'factorial', name: t('tools.factorial'), icon: 'asterisk' },
    { id: 'percentage', name: t('tools.percentage'), icon: 'percent' },
    { id: 'sqrt', name: t('tools.sqrt'), icon: 'function-square' },
    { id: 'gcdlcm', name: t('tools.gcdlcm'), icon: 'infinity' },
    { id: 'combination', name: t('tools.combination'), icon: 'combine' },
    { id: 'permutation', name: t('tools.permutation'), icon: 'shuffle' },
    { id: 'area', name: t('tools.area'), icon: 'square' },
    { id: 'volume', name: t('tools.volume'), icon: 'cube' },
    { id: 'perimeter', name: t('tools.perimeter'), icon: 'square-dashed' },
    { id: 'primeFactor', name: t('tools.primeFactor'), icon: 'divide' },
    { id: 'squareMeter', name: t('tools.squareMeter'), icon: 'ruler' },
    { id: 'baseConversion', name: t('tools.baseConversion'), icon: 'arrow-left-right' },
    { id: 'power', name: t('tools.power'), icon: 'chevrons-up' },
    { id: 'ratio', name: t('tools.ratio'), icon: 'divide-circle' },
    { id: 'modular', name: t('tools.modular'), icon: 'scan-face' },
    { id: 'random', name: t('tools.random'), icon: 'dice-1' },
    { id: 'pronunciation', name: t('tools.pronunciation'), icon: 'message-square-text' },
    { id: 'stdDev', name: t('tools.stdDev'), icon: 'chart-line-up' },
    { id: 'inch', name: t('tools.inch'), icon: 'ruler' },
    { id: 'mile', name: t('tools.mile'), icon: 'map-pin' },
    { id: 'waterNeed', name: t('tools.waterNeed'), icon: 'droplets', category: 'health' },
    { id: 'idealWeight', name: t('tools.idealWeight'), icon: 'weight', category: 'health' },
    { id: 'calorieNeed', name: t('tools.calorieNeed'), icon: 'flame', category: 'health' },
    { id: 'macroNutrient', name: t('tools.macroNutrient'), icon: 'pie-chart', category: 'health' },
    { id: 'proteinNeed', name: t('tools.proteinNeed'), icon: 'beef', category: 'health' },
    { id: 'carbNeed', name: t('tools.carbNeed'), icon: 'wheat', category: 'health' },
    { id: 'fatNeed', name: t('tools.fatNeed'), icon: 'sprout', category: 'health' },
    { id: 'goldenRatio', name: t('tools.goldenRatio'), icon: 'ratio' },
    { id: 'bmr', name: t('tools.bmr'), icon: 'activity', category: 'health' },
    { id: 'bmi', name: t('tools.bmi'), icon: 'scale', category: 'health' },
    { id: 'bodyFat', name: t('tools.bodyFat'), icon: 'activity', category: 'health' },
    { id: 'waistHipRatio', name: t('tools.waistHipRatio'), icon: 'shrink', category: 'health' },
    { id: 'babyHeight', name: t('tools.babyHeight'), icon: 'shrink', category: 'health' },
    { id: 'babyWeight', name: t('tools.babyWeight'), icon: 'shrink', category: 'health' },
    { id: 'birthDate', name: t('tools.birthDate'), icon: 'calendar', category: 'health' },
    { id: 'smokingCost', name: t('tools.smokingCost'), icon: 'cigarette', category: 'health' },
    { id: 'zekat', name: t('tools.zekat'), icon: 'coins', category: 'islamic' },
    { id: 'wordCount', name: t('tools.wordCount'), icon: 'file-text', category: 'tools' },
    { id: 'daysCalculator', name: t('tools.daysCalculator'), icon: 'calendar', category: 'time' },
    { id: 'gradeCalculator', name: t('tools.gradeCalculator'), icon: 'book-open', category: 'education' },
    { id: 'gpaCalculator', name: t('tools.gpaCalculator'), icon: 'book-open', category: 'education' },
    { id: 'calcApp', name: t('tools.calcApp'), icon: 'calculator' },
    { id: 'priceIncrease', name: t('tools.priceIncrease'), icon: 'trending-up', category: 'finance' },
    { id: 'goldCalculator', name: t('tools.goldCalculator'), icon: 'coins', category: 'finance' },
    { id: 'desiCalculator', name: t('tools.desiCalculator'), icon: 'box', category: 'measurement' },
    { id: 'tusPuan', name: t('tools.tusPuan'), icon: 'clipboard-edit', category: 'education' },
    { id: 'tytPuan', name: t('tools.tytPuan'), icon: 'book-check', category: 'education' },
    { id: 'loremIpsum', name: t('tools.loremIpsum'), icon: 'file-text', category: 'tools' },
    { id: 'cryptoCalculator', name: t('tools.cryptoCalculator'), icon: 'trending-up', category: 'finance' },
    { id: 'cryptoCurrencyConverter', name: t('tools.cryptoCurrencyConverter'), icon: 'coins', category: 'finance' },
    { id: 'yoldaGecenZaman', name: t('tools.yoldaGecenZaman'), icon: 'clock', category: 'time' },
    { id: 'dataUnitConverter', name: t('tools.dataUnitConverter'), icon: 'arrow-up-down', category: 'tools' },
    { id: 'weightUnitConverter', name: t('tools.weightUnitConverter'), icon: 'arrow-up-down', category: 'tools' },
    { id: 'energyUnitConverter', name: t('tools.energyUnitConverter'), icon: 'arrow-up-down', category: 'tools' },
    { id: 'lengthUnitConverter', name: t('tools.lengthUnitConverter'), icon: 'arrow-up-down', category: 'tools' },
    { id: 'dailyRentalIncome', name: t('tools.dailyRentalIncome'), icon: 'home', category: 'income' },
    { id: 'elektrikFaturasi', name: t('tools.elektrikFaturasi'), icon: 'zap', category: 'homeTools' },
    { id: 'dogalgazFaturasi', name: t('tools.dogalgazFaturasi'), icon: 'flame', category: 'homeTools' },
    { id: 'jsonFormatter', name: t('tools.jsonFormatter'), icon: 'file-text', category: 'tools' },
    { id: 'textCaseConverter', name: t('tools.textCaseConverter'), icon: 'type', category: 'tools' },
    { id: 'urlEncodeDecoder', name: t('tools.urlEncodeDecoder'), icon: 'link', category: 'tools' },
    { id: 'base64EncodeDecode', name: t('tools.base64EncodeDecode'), icon: 'file-text', category: 'tools' },
    { id: 'uuidGenerator', name: t('tools.uuidGenerator'), icon: 'hash', category: 'tools' },
    { id: 'hashCalculator', name: t('tools.hashCalculator'), icon: 'shield', category: 'tools' },
    { id: 'qrCodeGenerator', name: t('tools.qrCodeGenerator'), icon: 'qr-code', category: 'tools' },
    { id: 'imageResizer', name: t('tools.imageResizer'), icon: 'image', category: 'tools' },
    { id: 'textFormatter', name: t('tools.textFormatter'), icon: 'file-text', category: 'tools' },
    { id: 'passwordGenerator', name: t('tools.passwordGenerator'), icon: 'key', category: 'tools' },
    { id: 'colorPicker', name: t('tools.colorPicker'), icon: 'palette', category: 'tools' },
    { id: 'timeZoneConverter', name: t('tools.timeZoneConverter'), icon: 'clock', category: 'tools' },
    { id: 'cssMinifier', name: t('tools.cssMinifier'), icon: 'file-code', category: 'tools' },
    { id: 'jsBeautifier', name: t('tools.jsBeautifier'), icon: 'file-code', category: 'tools' },
    { id: 'textToASCII', name: t('tools.textToASCII'), icon: 'hash', category: 'tools' },
    { id: 'imageCompressor', name: t('tools.imageCompressor'), icon: 'image', category: 'tools' },
    { id: 'imageFormatConverter', name: t('tools.imageFormatConverter'), icon: 'image', category: 'tools' },
    { id: 'faviconGenerator', name: t('tools.faviconGenerator'), icon: 'image', category: 'tools' },
    { id: 'fuelConsumption', name: t('tools.fuelConsumption'), icon: 'fuel', category: 'automotive' },
    { id: 'obpCalculator', name: t('tools.obpCalculator'), icon: 'book-open', category: 'education' },
    { id: 'animalSounds', name: t('tools.animalSounds'), icon: 'paw-print', category: 'education' },
    { id: 'klimaBtu', name: t('tools.klimaBtu'), icon: 'zap', category: 'homeTools' },
    { id: 'liseMezuniyet', name: t('tools.liseMezuniyet'), icon: 'graduation-cap', category: 'education' },
    { id: 'yumurtlamaDonemi', name: t('tools.yumurtlamaDonemi'), icon: 'calendar-heart', category: 'health' },
    { id: 'yumurtlamaDonemi', name: t('tools.yumurtlamaDonemi'), icon: 'calendar-heart', category: 'health' },
    { id: 'braSize', name: t('tools.braSize'), icon: 'person-standing', category: 'health' },
    { id: 'metabolizmaYasi', name: t('tools.metabolizmaYasi'), icon: 'activity', category: 'health' },
    { id: 'dollarEuro', name: t('tools.dollarEuro'), icon: 'arrow-left-right', category: 'finance' }
  ].filter(calc => language === 'tr' || allowedNlIds.includes(calc.id));

  // Sayfa başlığı ve meta bilgileri için
  const currentCalculator = calculators.find(calc => calc.id === activeCalculator);
  const pageTitle = useMemo(() => {
    if (activeCalculator === 'home' && language === 'tr') {
      return 'Hesapla ya da Hesaplama - Hesaplama.Vip';
    }

    if (!currentCalculator) return language === 'nl' ? 'Hesaplama.Vip - Online Rekenmachines' : 'Hesapla ya da Hesaplama - Hesaplama.Vip';

    let title = currentCalculator.name;

    // Localize the name from translations if available
    const localizedName = t(`tools.${activeCalculator}`);
    if (localizedName && localizedName !== `tools.${activeCalculator}`) {
      title = localizedName;
    }

    // Doğalgaz faturası için dinamik tarih ekle
    if (activeCalculator === 'dogalgazFaturasi' && language === 'tr') {
      const date = new Date().toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
      // İlk harfi büyük yap (Aralık 2025)
      const formattedDate = date.charAt(0).toUpperCase() + date.slice(1);
      title = `${title} - ${formattedDate}`;
    }

    // Kripto Hesaplayıcı için özel SEO başlığı
    if (activeCalculator === 'cryptoCalculator' && language === 'tr') {
      return "Kripto Coin Kâr/Zarar Hesaplama - Spot/Vadeli - Toplam Kâr Göster";
    }

    return `${title}${t('seo.titleSuffix')}`;
  }, [currentCalculator, activeCalculator, language, t]);

  const pageDescription = useMemo(() => {
    if (!currentCalculator) return language === 'nl' ? 'Verzameling van moderne rekenmachines voor al uw dagelijkse berekeningen.' : 'Modern hesaplama araçları koleksiyonu ile hızlı ve doğru hesaplamalar yapın.';

    return language === 'nl'
      ? `Bereken snel en eenvoudig met de ${t(`tools.${activeCalculator}`) || currentCalculator.name} tool.`
      : `${t(`tools.${activeCalculator}`) || currentCalculator.name} hesaplama aracı ile hızlı ve kolay hesaplama yapın.`;
  }, [currentCalculator, activeCalculator, language, t]);
  const canonicalUrl = `https://hesaplama.vip${location.pathname}`;

  const getComponentForCalculator = (id: string) => {
    switch (id) {
      case 'home':
        return <HomePage setActiveCalculator={handleSetActiveCalculator} />;
      case 'calcApp':
        return <Calculator />;
      case 'area':
        return <AreaCalculator />;
      case 'goldenRatio':
        return <GoldenRatioCalculator />;
      case 'primeFactor':
        return <PrimeFactorCalculator />;
      case 'perimeter':
        return <PerimeterCalculator />;
      case 'gcdlcm':
        return <GcdLcmCalculator />;
      case 'factorial':
        return <FactorialCalculator />;
      case 'combination':
        return <CombinationCalculator />;
      case 'permutation':
        return <PermutationCalculator />;
      case 'volume':
        return <VolumeCalculator />;
      case 'squareMeter':
        return <SquareMeterCalculator />;
      case 'baseConversion':
        return <BaseConversionCalculator />;
      case 'inch':
        return <InchCalculator />;
      case 'mile':
        return <MileCalculator />;
      case 'sqrt':
        window.location.href = 'https://karekok.hesaplama.vip/';
        return null;
      case 'power':
        return <PowerCalculator />;
      case 'ratio':
        return <RatioCalculator />;
      case 'percentage':
        window.location.href = 'https://yuzde.hesaplama.vip/';
        return null;
      case 'modular':
        return <ModularCalculator />;
      case 'random':
        return <RandomNumberGenerator />;
      case 'pronunciation':
        window.location.href = 'https://sayiokunusu.hesaplama.vip/';
        return null;
      case 'goldCalculator':
        window.location.href = 'https://altin.hesaplama.vip/';
        return null;
      case 'stdDev':
        return <StandardDeviationCalculator />;
      case 'waterNeed':
        return <WaterNeedCalculator />;
      case 'idealWeight':
        return <IdealWeightCalculator />;
      case 'calorieNeed':
        return <CalorieNeedCalculator />;
      case 'macroNutrient':
        return <MacroNutrientCalculator />;
      case 'proteinNeed':
        return <ProteinNeedCalculator />;
      case 'carbNeed':
        return <CarbNeedCalculator />;
      case 'fatNeed':
        return <FatNeedCalculator />;
      case 'bmr':
        return <BmrCalculator />;
      case 'babyHeight':
        return <BabyHeightCalculator />;
      case 'babyWeight':
        return <BabyWeightCalculator />;
      case 'waistHipRatio':
        return <WaistHipRatioCalculator />;
      case 'birthDate':
        return <BirthDateCalculator />;
      case 'smokingCost':
        return <SmokingCostCalculator />;
      case 'bmi':
        return <BmiCalculator />;
      case 'bodyFat':
        return <BodyFatCalculator />;
      case 'zekat':
        return <ZekatCalculator />;
      case 'wordCount':
        return <WordCountCalculator />;
      case 'daysCalculator':
        return <DaysCalculator />;
      case 'gradeCalculator':
        return <GradeCalculator />;
      case 'gpaCalculator':
        return <GpaCalculator />;
      case 'priceIncrease':
        return <PriceIncreaseCalculator />;
      case 'klimaBtu':
        return <KlimaBtuCalculator />;
      case 'yasHesaplama':
        if (location.pathname.match(/^\/yas-hesaplama\/.+$/)) {
          return <AgeCalculationPage />;
        }
        return <YasHesaplamaCalculator />;
      case 'liseMezuniyet':
        return <LiseMezuniyetPuaniCalculator />;
      case 'metabolizmaYasi':
        return <MetabolizmaYasiCalculator />;
      case 'yumurtlamaDonemi':
        return <YumurtlamaDonemiCalculator />;
      case 'tusPuan':
        return <TusPuanCalculator />;
      case 'tytPuan':
        return <TytPuanCalculator />;
      case 'about':
        return <AboutPage />;
      case 'contact':
        return <ContactPage />;
      case 'privacyPolicy':
        return <PrivacyPolicyPage />;

      case 'dollarTlCalculator':
        return <DolarTlCalculator />;
      case 'dollarEuro':
        return <DollarEuroCalculator />;
      case 'tlDolarCalculator':
        return <TlDolarCalculator />;
      case 'euroDolarCalculator':
        return <EuroDolarCalculator />;
      case 'obpCalculator':
        return <ObpCalculator />;
      case 'tlEuroCalculator':
        return <TlEuroCalculator />;
      case 'euroTlCalculator':
        return <EuroTlCalculator />;
      case 'loremIpsum':
        return <LoremIpsumGenerator />;
      case 'cryptoCalculator':
        return <CryptoCalculator />;
      case 'cryptoCurrencyConverter':
        return <CryptoCurrencyConverter />;
      case 'yoldaGecenZaman':
        return <YoldaGecenZamanCalculator />;
      case 'dataUnitConverter':
        return <DataUnitConverter />;
      case 'weightUnitConverter':
        if (location.pathname.match(/^\/agirlik-cevirici\/.+$/)) {
          return <WeightConverterPage />;
        }
        return <WeightUnitConverter />;
      case 'energyUnitConverter':
        return <EnergyUnitConverter />;
      case 'lengthUnitConverter':
        if (location.pathname.match(/^\/uzunluk-birimi-donusturucu\/.+$/)) {
          return <LengthConverterPage />;
        }
        return <LengthUnitConverter />;
      case 'dailyRentalIncome':
        return <DailyRentalIncomeCalculator />;
      case 'elektrikFaturasi':
        return <ElektrikFaturasiCalculator />;
      case 'dogalgazFaturasi':
        return <DoğalgazFaturasiCalculator />;
      case 'jsonFormatter':
        return <JsonFormatterCalculator />;
      case 'textCaseConverter':
        return <TextCaseConverter />;
      case 'urlEncodeDecoder':
        return <UrlEncodeDecodeCalculator />;
      case 'base64EncodeDecode':
        return <Base64EncodeDecodeCalculator />;
      case 'uuidGenerator':
        return <UuidGeneratorCalculator />;
      case 'hashCalculator':
        return <HashCalculatorCalculator />;
      case 'qrCodeGenerator':
        return <QrCodeGeneratorCalculator />;
      case 'imageResizer':
        return <ImageResizerCalculator />;
      case 'textFormatter':
        return <TextFormatterCalculator />;
      case 'passwordGenerator':
        return <PasswordGeneratorCalculator />;
      case 'colorPicker':
        return <ColorPickerCalculator />;
      case 'timeZoneConverter':
        return <TimeZoneConverterCalculator />;
      case 'cssMinifier':
        return <CSSMinifierCalculator />;
      case 'jsBeautifier':
        return <JSBeautifierCalculator />;
      case 'textToASCII':
        return <TextToASCIICalculator />;
      case 'turkishCharCleaner':
        return (
          <React.Suspense fallback={<div className="p-8 text-center text-gray-400">Yükleniyor...</div>}>
            <TurkishCharacterCleanerCalculator />
          </React.Suspense>
        );
      case 'imageCompressor':
        return <ImageCompressorCalculator />;
      case 'imageFormatConverter':
        return <ImageFormatConverterCalculator />;
      case 'faviconGenerator':
        return (
          <React.Suspense fallback={<div className="p-8 text-center text-gray-400">Yükleniyor...</div>}>
            <FaviconGeneratorCalculator />
          </React.Suspense>
        );
      case 'fuelConsumption':
        return <FuelConsumptionCalculator />;
      case 'animalSounds':
        return (
          <React.Suspense fallback={<div className="p-8 text-center text-gray-400">Yükleniyor...</div>}>
            <AnimalSoundsPage />
          </React.Suspense>
        );
      case 'braSize':
        return <BraSizeCalculator />;
      case 'desiCalculator':
        return <DesiCalculator />;
      case 'gone410':
        return <GonePage />;
      default:
        console.warn(`Bilinmeyen hesaplayıcı ID'si: ${id}, ana sayfaya yönlendiriliyor.`);
        return <div className="p-4">Hesaplayıcı bulunamadı</div>;
    }
  };


  // Mobil Sidebar durumu
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);

  // Ekran boyutunu izle
  useEffect(() => {
    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 768);
    };

    // İlk kontrol
    checkIsDesktop();

    window.addEventListener('resize', checkIsDesktop);
    return () => window.removeEventListener('resize', checkIsDesktop);
  }, []);

  return (
    <div className="flex flex-col md:flex-row min-h-screen w-full bg-[var(--background-color)] text-[var(--text-color)] font-mono">
      <PageScrollHandler />
      {/* SEO Meta Etiketleri */}
      <SEOMetaTags
        key={location.pathname}
        id={activeCalculator}
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
      />

      <div className="fixed top-0 left-0 w-full bg-[var(--background-color)] border-b border-[var(--border-color)] p-4 md:hidden z-10 flex items-center justify-between">
        <a
          href="/"
          className="flex items-center cursor-pointer"
          onClick={(e) => {
            e.preventDefault();
            handleSetActiveCalculator('home');
          }}
        >
          <div className="mr-3">
            <CalculatorLogo size="xs" />
          </div>
          <h1 className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-600">{t('common.title')}</h1>
        </a>
        <div className="flex items-center gap-2">
          <ThemeToggle />
          <button
            className="p-2 rounded bg-cyan-900/50 text-cyan-300 hover:bg-cyan-800/50 transition-colors"
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
          >
            {isMobileSidebarOpen ? 'Kapat' : 'Menü'}
          </button>
        </div>
      </div>

      {/* 
        Sidebar Rendering Logic:
        - Desktop: Always render (but hidden by CSS if necessary, though here we rely on md:block)
        - Mobile: Render ONLY if isMobileSidebarOpen is true.
        This ensures that on initial mobile load, the Sidebar chunk is NOT downloaded.
      */}
      {(isDesktop || isMobileSidebarOpen) && (
        <div id="mobile-sidebar" className={`${isMobileSidebarOpen ? 'block fixed inset-0 z-20 top-[73px]' : 'hidden'} md:block md:w-64 p-4 bg-[var(--sidebar-bg)] border-r border-[var(--border-color)]`}>
          <Suspense fallback={<div className="animate-pulse text-cyan-400 p-4">Menü...</div>}>
            <Sidebar
              calculators={calculators}
              activeCalculator={activeCalculator}
              setActiveCalculator={handleSetActiveCalculator}
              onCloseMobile={() => setIsMobileSidebarOpen(false)}
            />
          </Suspense>
        </div>
      )}

      <main
        className="flex-1 pb-4 px-4 sm:px-8 md:px-12 lg:px-[53px] pt-20 md:pt-4 overflow-auto"
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
      >
        {activeCalculator === 'home' ? (
          <div className="max-w-7xl mx-auto w-full">
            <Suspense fallback={<LoadingFallback />}>
              {getComponentForCalculator(activeCalculator)}
            </Suspense>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            width: '100%'
          }}>
            <div style={{
              width: '100%',
              maxWidth: activeCalculator === 'goldCalculator' ? '800px' : '672px',
              minHeight: '400px'
            }}>
              <Suspense fallback={<LoadingFallback />}>
                {getComponentForCalculator(activeCalculator)}
              </Suspense>
            </div>
          </div>
        )}
      </main>

      {/* Grid background overlay */}
      <div className="fixed inset-0 pointer-events-none z-[-1]">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(16,_138,_230,_0.03)_1px,transparent_1px)] bg-[length:20px_20px]"></div>
      </div>
    </div>
  );
}

function App() {
  const [theme, setTheme] = useState<'light' | 'dark'>('dark');

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    if (newTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('theme', newTheme);
  };

  // Theme'i localStorage'dan veya sistem tercihinden yükleme
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null;

    if (savedTheme) {
      setTheme(savedTheme);
      if (savedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    } else {
      // Kullanıcı tercihi yoksa varsayılan olarak dark tema kullan
      setTheme('dark');
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }

    // Domain kontrolü ve güvenlik
    //    const a = "aGVzYXBsYW1hLnZpcA==";
    //   const b = "aHR0cHM6Ly9oZXNhplama.vip";
    //   const c = (d: string) => atob(d);
    //   const e = c(a);
    //   if (window.location.hostname !== e) {
    //   setTimeout(() => {
    //   window.location.href = c(b);
    //     }, 1000);
    //   }

  }, []);

  return (
    <HelmetProvider>
      <ThemeContext.Provider value={{ theme, toggleTheme }}>
        <Router>
          <ScrollToTop />
          <LanguageProvider>
            <Routes>
              {/* Ana sayfa */}
              <Route path="/" element={<AppContent />} />

              {/* Türkçe URL'ler */}
              <Route path="/faktoriyel" element={<AppContent />} />
              <Route path="/obp-hesaplama" element={<AppContent />} />
              <Route path="/yumurtlama-donemi-hesaplama" element={<AppContent />} />
              <Route path="/sutyen-olcusu-hesaplama" element={<AppContent />} />
              <Route path="/desi-hesaplama" element={<AppContent />} />
              <Route path="/yuzde-hesaplama" element={<AppContent />} />
              <Route path="/yuzde-hesaplama/:slug" element={<AppContent />} />
              <Route path="/koklu-sayi" element={<AppContent />} />
              <Route path="/koklu-sayi/:slug" element={<AppContent />} />
              <Route path="/ebob-ekok" element={<AppContent />} />
              <Route path="/kombinasyon" element={<AppContent />} />
              <Route path="/permutasyon" element={<AppContent />} />
              <Route path="/alan-hesaplama" element={<AppContent />} />
              <Route path="/hacim-hesaplama" element={<AppContent />} />
              <Route path="/cevre-hesaplama" element={<AppContent />} />
              <Route path="/asal-carpan" element={<AppContent />} />
              <Route path="/metrekare" element={<AppContent />} />
              <Route path="/taban-donusumu" element={<AppContent />} />
              <Route path="/inc" element={<AppContent />} />
              <Route path="/mil" element={<AppContent />} />
              <Route path="/uslu-sayi" element={<AppContent />} />
              <Route path="/oran" element={<AppContent />} />
              <Route path="/moduler" element={<AppContent />} />
              <Route path="/rastgele-sayi" element={<AppContent />} />
              <Route path="/sayi-okunusu/:slug" element={<AppContent />} />
              <Route path="/sayi-okunusu" element={<AppContent />} />
              <Route path="/altin-hesaplama/*" element={<AppContent />} />
              <Route path="/altin-hesaplama" element={<AppContent />} />
              <Route path="/standart-sapma" element={<AppContent />} />
              <Route path="/kelime-sayisi" element={<AppContent />} />
              <Route path="/lorem-ipsum-uretici" element={<AppContent />} />
              <Route path="/veri-birimi-cevirici" element={<AppContent />} />
              <Route path="/agirlik-birimi-donusturucu" element={<AppContent />} />
              <Route path="/enerji-birimi-donusturucu" element={<AppContent />} />
              <Route path="/uzunluk-birimi-donusturucu" element={<AppContent />} />
              <Route path="/metabolizma-yasi" element={<AppContent />} />
              <Route path="/gunluk-ev-kiralama-kazanc-hesaplama" element={<AppContent />} />
              <Route path="/elektrik-faturasi-hesaplama" element={<AppContent />} />
              <Route path="/dogalgaz-faturasi-hesaplama" element={<AppContent />} />
              <Route path="/json-bicimlendirici" element={<AppContent />} />
              <Route path="/metin-harf-donusturucu" element={<AppContent />} />
              <Route path="/url-encode-decode" element={<AppContent />} />
              <Route path="/base64-encode-decode" element={<AppContent />} />
              <Route path="/uuid-generator" element={<AppContent />} />
              <Route path="/hash-hesaplayici" element={<AppContent />} />
              <Route path="/qr-kod-olusturucu" element={<AppContent />} />
              <Route path="/gorsel-boyutlandirma" element={<AppContent />} />
              <Route path="/metin-bicimlendirici" element={<AppContent />} />
              <Route path="/sifre-uretici" element={<AppContent />} />
              <Route path="/renk-secici" element={<AppContent />} />
              <Route path="/saat-dilimi-donusturucu" element={<AppContent />} />
              <Route path="/css-minifier" element={<AppContent />} />
              <Route path="/js-beautifier" element={<AppContent />} />
              <Route path="/text-to-ascii" element={<AppContent />} />
              <Route path="/image-compressor" element={<AppContent />} />
              <Route path="/image-format-converter" element={<AppContent />} />
              <Route path="/favicon-generator" element={<AppContent />} />
              <Route path="/yakit-tuketimi-hesaplama" element={<AppContent />} />
              <Route path="/kac-gun-oldu-hesaplama" element={<AppContent />} />

              {/* Dutch routes - Explicitly defined to ensure params are handled correctly */}
              <Route path="/nl/percentage-berekenen" element={<AppContent />} />
              <Route path="/nl/percentage-berekenen/:slug" element={<AppContent />} />
              <Route path="/nl/getal-schrijven" element={<AppContent />} />
              <Route path="/nl/getal-schrijven/:slug" element={<AppContent />} />
              <Route path="/nl/wortel-berekenen" element={<AppContent />} />
              <Route path="/nl/wortel-berekenen/:slug" element={<AppContent />} />
              <Route path="/nl/dollar-tl-berekenen" element={<AppContent />} />
              <Route path="/nl/euro-tl-berekenen" element={<AppContent />} />
              <Route path="/nl/dollar-naar-euro" element={<AppContent />} />
              <Route path="/nl/dollar-naar-euro/:slug" element={<AppContent />} />

              <Route path="/hayvan-sesleri" element={<AppContent />} />

              {/* Ek ID tabanlı URL'ler (geriye dönük uyumluluk için) */}
              <Route path="/lorem-ipsum" element={<AppContent />} />
              <Route path="/dataUnitConverter" element={<AppContent />} />
              <Route path="/weightUnitConverter" element={<AppContent />} />
              <Route path="/energyUnitConverter" element={<AppContent />} />
              <Route path="/lengthUnitConverter" element={<AppContent />} />
              <Route path="/metabolizmaYasi" element={<AppContent />} />

              <Route path="/ders-notu-hesaplama" element={<AppContent />} />

              {/* Diğer tüm URL'ler için catch-all */}
              <Route path="/*" element={<AppContent />} />
            </Routes>
          </LanguageProvider>
        </Router>
      </ThemeContext.Provider >
    </HelmetProvider>
  );
}

// Tema değiştirme komponenti
const ThemeToggle: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const { t } = useLanguage();

  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-full bg-cyan-900/50 dark:text-yellow-300 text-slate-700
                hover:bg-cyan-800/50 transition-colors"
      aria-label={theme === 'dark' ? t('common.toggleLight') : t('common.toggleDark')}
    >
      {theme === 'dark' ? (
        <Sun size={18} />
      ) : (
        <Moon size={18} />
      )}
    </button>
  );
};

export default App;